import * as THREE from "three";
import { dom } from "../dom";
import { state, type MeasureHistoryItem } from "../state";
import {
  getActiveCamera,
  measureLayer,
  measureLineMaterial,
  measurePointMaterial,
  pointerNdc,
} from "../viewer/scene";
import { fragmentsRaycast } from "../fragments/ifc-shim";
import { SnappingClass } from "@thatopen/fragments";
import { setStatus } from "../ui/status";
import { onLangChange, t } from "../i18n";

export function formatMeasureDistance(value: number): string {
  if (!Number.isFinite(value)) {
    return "n/a";
  }
  const factor = state.measureUnit === "mm" ? 1000 : 100;
  const suffix = state.measureUnit === "mm" ? "мм" : "см";
  return `${(value * factor).toFixed(state.measureUnit === "mm" ? 0 : 1)} ${suffix}`;
}

function makeMeasureLabelSprite(text: string): THREE.Sprite | null {
  const canvas2d = document.createElement("canvas");
  canvas2d.width = 256;
  canvas2d.height = 96;
  const ctx = canvas2d.getContext("2d");
  if (!ctx) {
    return null;
  }
  ctx.clearRect(0, 0, canvas2d.width, canvas2d.height);
  ctx.fillStyle = "rgba(15,23,42,0.78)";
  ctx.strokeStyle = "rgba(56,189,248,0.85)";
  ctx.lineWidth = 3;
  // roundRect — экспериментальный API, но поддерживается во всех целевых браузерах
  (ctx as any).roundRect?.(4, 8, 248, 80, 14);
  ctx.fill();
  ctx.stroke();
  ctx.font = "600 34px Arial";
  ctx.fillStyle = "#e2e8f0";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, canvas2d.width / 2, canvas2d.height / 2 + 1);

  const texture = new THREE.CanvasTexture(canvas2d);
  texture.needsUpdate = true;
  const material = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: false,
    depthWrite: false,
  });
  const sprite = new THREE.Sprite(material);
  sprite.scale.set(0.75, 0.28, 1);
  sprite.renderOrder = 1000;
  return sprite;
}

export function refreshMeasurementLabel(item: MeasureHistoryItem): void {
  const text = formatMeasureDistance(item.distance);
  if (item.labelSprite) {
    const oldMat = item.labelSprite.material as THREE.SpriteMaterial;
    oldMat?.map?.dispose();
    oldMat?.dispose();
    item.object3d.remove(item.labelSprite);
  }
  const sprite = makeMeasureLabelSprite(text);
  if (!sprite) {
    item.labelSprite = null;
    return;
  }
  const mid = item.firstPoint.clone().add(item.secondPoint).multiplyScalar(0.5);
  sprite.position.copy(mid.add(new THREE.Vector3(0, 0.06, 0)));
  item.object3d.add(sprite);
  item.labelSprite = sprite;
}

export function renderMeasureHistory(): void {
  dom.clearMeasureHistoryButton.disabled = state.measureHistory.length === 0;
  if (state.measureHistory.length === 0) {
    dom.measureHistoryElement.innerHTML = `<li>${t("measure.history.empty")}</li>`;
    return;
  }
  const deleteLabel = t("measure.history.delete");
  dom.measureHistoryElement.innerHTML = state.measureHistory
    .map(
      (item) =>
        `<li><span>#${item.id}: ${formatMeasureDistance(item.distance)}</span><button class="measure-delete-btn" data-measure-id="${item.id}" type="button">${deleteLabel}</button></li>`,
    )
    .join("");
}

export function updateMeasureUi(): void {
  dom.toggleMeasureButton.textContent = state.measureMode
    ? t("btn.measureOn")
    : t("btn.measureOff");
  if (!state.ifcModel) {
    dom.measureInfoElement.textContent = t("measure.info.loadIfc");
  } else if (!state.measureMode) {
    dom.measureInfoElement.textContent = t("measure.info.off");
  } else if (!state.pendingMeasurePoint) {
    dom.measureInfoElement.textContent = t("measure.info.pickFirst");
  } else {
    dom.measureInfoElement.textContent = t("measure.info.pickSecond");
  }
}

onLangChange(() => {
  updateMeasureUi();
  renderMeasureHistory();
});

export function clearPendingMeasurePoint(): void {
  state.pendingMeasurePoint = null;
  if (state.pendingMeasureMarker) {
    measureLayer.remove(state.pendingMeasureMarker);
    state.pendingMeasureMarker.geometry.dispose();
    state.pendingMeasureMarker = null;
  }
}

export function clearAllMeasurements(): void {
  clearPendingMeasurePoint();
  for (const item of state.measureHistory) {
    measureLayer.remove(item.object3d);
    item.object3d.traverse((obj: any) => {
      if (obj.geometry) {
        obj.geometry.dispose();
      }
      if (obj.isSprite && obj.material) {
        if (obj.material.map) {
          obj.material.map.dispose();
        }
        obj.material.dispose();
      }
    });
  }
  state.measureHistory.length = 0;
  renderMeasureHistory();
  updateMeasureUi();
}

function createMeasurePoint(position: THREE.Vector3): THREE.Mesh {
  const pointGeo = new THREE.SphereGeometry(0.04, 12, 8);
  const point = new THREE.Mesh(pointGeo, measurePointMaterial);
  point.position.copy(position);
  return point;
}

export function addMeasurement(firstPoint: THREE.Vector3, secondPoint: THREE.Vector3): void {
  const distance = firstPoint.distanceTo(secondPoint);
  const lineGeo = new THREE.BufferGeometry().setFromPoints([firstPoint, secondPoint]);
  const line = new THREE.Line(lineGeo, measureLineMaterial);
  const group = new THREE.Group();
  group.add(line);
  group.add(createMeasurePoint(firstPoint));
  group.add(createMeasurePoint(secondPoint));
  measureLayer.add(group);

  state.measureHistory.push({
    id: state.measureCounter++,
    distance,
    object3d: group,
    firstPoint: firstPoint.clone(),
    secondPoint: secondPoint.clone(),
    labelSprite: null,
  });
  refreshMeasurementLabel(state.measureHistory[state.measureHistory.length - 1]);

  while (state.measureHistory.length > 5) {
    const removed = state.measureHistory.shift();
    if (removed) {
      measureLayer.remove(removed.object3d);
      removed.object3d.traverse((obj: any) => {
        if (obj.geometry) {
          obj.geometry.dispose();
        }
      });
    }
  }
  renderMeasureHistory();
  setStatus(`Замер: ${formatMeasureDistance(distance)}`);
}

export function removeMeasurementById(id: number): void {
  const index = state.measureHistory.findIndex((item) => item.id === id);
  if (index < 0) {
    return;
  }
  const [item] = state.measureHistory.splice(index, 1);
  measureLayer.remove(item.object3d);
  item.object3d.traverse((obj: any) => {
    if (obj.geometry) {
      obj.geometry.dispose();
    }
    if (obj.isSprite && obj.material) {
      if (obj.material.map) {
        obj.material.map.dispose();
      }
      obj.material.dispose();
    }
  });
  renderMeasureHistory();
  setStatus(`Замер #${id} удалён.`);
}

function trianglePointsFromFacePoints(fp: unknown): THREE.Vector3[] | null {
  // Fragments raycast возвращает facePoints как плоский массив мировых координат
  // [x,y,z, x,y,z, x,y,z].
  if (!Array.isArray(fp) && !(fp instanceof Float32Array) && !(fp instanceof Float64Array)) {
    return null;
  }
  const arr = fp as ArrayLike<number>;
  if (arr.length < 9) {
    return null;
  }
  const tri: THREE.Vector3[] = [];
  for (let k = 0; k < 3; k++) {
    tri.push(new THREE.Vector3(arr[k * 3], arr[k * 3 + 1], arr[k * 3 + 2]));
  }
  return tri;
}

function snapClasses(): SnappingClass[] | undefined {
  if (state.measureSnapMode === "vertex") {
    return [SnappingClass.POINT];
  }
  if (state.measureSnapMode === "edge") {
    return [SnappingClass.LINE];
  }
  if (state.measureSnapMode === "center") {
    return [SnappingClass.FACE];
  }
  return undefined;
}

function pointFromHit(hit: { point?: THREE.Vector3 }): THREE.Vector3 | null {
  if (!hit.point) {
    return null;
  }
  return new THREE.Vector3(hit.point.x, hit.point.y, hit.point.z);
}

function isNearCursor(point: THREE.Vector3, event: PointerEvent): boolean {
  const projected = point.clone().project(getActiveCamera());
  if (projected.z < -1 || projected.z > 1) {
    return false;
  }
  const rect = dom.canvas.getBoundingClientRect();
  const x = (projected.x * 0.5 + 0.5) * rect.width + rect.left;
  const y = (-projected.y * 0.5 + 0.5) * rect.height + rect.top;
  const dx = x - event.clientX;
  const dy = y - event.clientY;
  return dx * dx + dy * dy <= 56 * 56;
}

function snapOnTriangle(hitPoint: THREE.Vector3, tri: THREE.Vector3[]): THREE.Vector3 {
  if (state.measureSnapMode === "vertex") {
    tri.sort((a, b) => a.distanceTo(hitPoint) - b.distanceTo(hitPoint));
    return tri[0].clone();
  }
  if (state.measureSnapMode === "center") {
    return tri[0].clone().add(tri[1]).add(tri[2]).multiplyScalar(1 / 3);
  }
  if (state.measureSnapMode === "edge") {
    const edges: [THREE.Vector3, THREE.Vector3][] = [
      [tri[0], tri[1]],
      [tri[1], tri[2]],
      [tri[2], tri[0]],
    ];
    let best: THREE.Vector3 | null = null;
    let bestDist = Number.POSITIVE_INFINITY;
    const temp = new THREE.Vector3();
    for (const edge of edges) {
      const line = new THREE.Line3(edge[0], edge[1]);
      line.closestPointToPoint(hitPoint, true, temp);
      const d = temp.distanceToSquared(hitPoint);
      if (d < bestDist) {
        bestDist = d;
        best = temp.clone();
      }
    }
    return best || hitPoint.clone();
  }
  return hitPoint.clone();
}

async function pickPointOnIfc(event: PointerEvent): Promise<THREE.Vector3 | null> {
  if (!state.ifcModel) {
    return null;
  }
  // Fragments.raycast ожидает координаты курсора в CSS-пикселях (clientX/Y).
  pointerNdc.x = event.clientX;
  pointerNdc.y = event.clientY;
  const classes = snapClasses();
  const hit = (await fragmentsRaycast(getActiveCamera(), pointerNdc, dom.canvas, classes)) as
    | {
        point?: THREE.Vector3;
        facePoints?: unknown;
        snappedEdgeP1?: THREE.Vector3;
        snappedEdgeP2?: THREE.Vector3;
      }
    | undefined;
  if (!hit?.point) {
    return null;
  }
  const hitPoint = pointFromHit(hit);
  if (!hitPoint) {
    return null;
  }
  if (state.measureSnapMode === "surface") {
    return hitPoint;
  }
  let snapped: THREE.Vector3 | null = hitPoint.clone();
  if (
    state.measureSnapMode === "edge" &&
    hit.snappedEdgeP1 &&
    hit.snappedEdgeP2
  ) {
    const closest = new THREE.Vector3();
    new THREE.Line3(hit.snappedEdgeP1, hit.snappedEdgeP2).closestPointToPoint(
      hitPoint,
      true,
      closest,
    );
    snapped = closest;
  } else if (state.measureSnapMode === "center") {
    const tri = trianglePointsFromFacePoints(hit.facePoints);
    snapped = tri ? snapOnTriangle(hitPoint, tri) : hitPoint.clone();
  }
  if (snapped && isNearCursor(snapped, event)) {
    return snapped;
  }
  const plain = await fragmentsRaycast(getActiveCamera(), pointerNdc, dom.canvas);
  const plainPoint = plain?.point ? pointFromHit(plain) : null;
  const plainTri = trianglePointsFromFacePoints(
    (plain as { facePoints?: unknown } | undefined)?.facePoints,
  );
  if (plainPoint && plainTri) {
    const local = snapOnTriangle(plainPoint, plainTri);
    if (isNearCursor(local, event)) {
      return local;
    }
  }
  return plainPoint ?? snapped;
}

export async function handleMeasureClick(event: PointerEvent): Promise<void> {
  const point = await pickPointOnIfc(event);
  if (!point) {
    setStatus("Линейка: кликните по поверхности IFC.", true);
    return;
  }

  if (!state.pendingMeasurePoint) {
    state.pendingMeasurePoint = point;
    state.pendingMeasureMarker = createMeasurePoint(point);
    measureLayer.add(state.pendingMeasureMarker);
    updateMeasureUi();
    return;
  }

  addMeasurement(state.pendingMeasurePoint, point);
  clearPendingMeasurePoint();
  updateMeasureUi();
}
